// Dependencies
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _ from 'lodash';
import classNames from 'classnames';
import Bookmark from '@nerdwallet/react-icon/fontawesome/Bookmark';
import {
  setTrk,
  TrkTag,
  TrkElement,
} from '@nerdwallet/nw-client-lib/trk-helper';
import uuid from 'uuid/v4';

import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { Impression } from '@nerdwallet/analytics/react/impression';

import { Text, Box, Link } from '@nerdwallet/currency';

import { stringInputSpec, boolCheckboxSpec } from '../../lib/editing-configs';

const AwardsNavigationBox = ({
  section,
  sectionUrl,
  showTrailingDisc,
  awardsYear,
  index,
  currentCategory,
  size,
  shouldTrack,
}) => {
  const isNewLayout = awardsYear === '2022';
  const isSmall = size === 'sm' && isNewLayout;
  const selected = sectionUrl === currentCategory && isNewLayout;
  const bookmark = index === 0 && isNewLayout;
  const isNotBookmark = index !== 0 && isNewLayout;

  const nodeId = uuid();
  const [trkId] = useState(nodeId);

  const elementImpressionProps = {
    id: `awards-navigation-${trkId}`,
    elementName: 'AwardsNavigation',
  };

  const isAnchorLink = (sectionUrl || '').charAt(0) === '#';
  const modifiedUrl = isAnchorLink
    ? sectionUrl
    : sectionUrl &&
      setTrk({
        url: sectionUrl,
        params: {
          [TrkTag.location]: 'AwardsNavigation',
          [TrkTag.element]: TrkElement.hyperlink,
          [TrkTag.copy]: section?.toLowerCase(),
          [TrkTag.sectionCategory]: 'AwardsNavigation--footer',
        },
      });
  const url = shouldTrack ? modifiedUrl : sectionUrl;
  const circleIcon = (
    <span className={classNames(['awards-nav__disc', `layout-${awardsYear}`])}>
      &bull;
    </span>
  );

  const sectionSize = isSmall ? 'small' : 'default';

  const bookmarkIcon = (
    <Box
      className={classNames([
        'awards-nav__bookmark',
        `layout-${awardsYear}`,
        {
          selected: selected && bookmark,
        },
      ])}
    >
      <Bookmark />
    </Box>
  );

  const navigationOutput = (
    <Link href={url} className={classNames(['awards-nav__link'])}>
      <Box
        className={classNames([
          'awards-nav__section',
          `layout-${awardsYear}`,
          { selected },
        ])}
      >
        <Text size={sectionSize}>{section}</Text>
      </Box>
    </Link>
  );

  const navigationOutputWithTrk = (
    <Impression eventType="element_impression" entityName="awards_navigation">
      <ElementImpression {...elementImpressionProps}>
        {navigationOutput}
      </ElementImpression>
    </Impression>
  );

  const awardsNavigationOutput = shouldTrack
    ? navigationOutputWithTrk
    : navigationOutput;

  return (
    <div className={classNames(['awards-nav__box', `layout-${awardsYear}`])}>
      {showTrailingDisc && isNotBookmark && circleIcon}
      {bookmark && bookmarkIcon}
      {awardsNavigationOutput}
      {showTrailingDisc && !isNewLayout && circleIcon}
    </div>
  );
};

AwardsNavigationBox.propTypes = {
  section: PropTypes.string,
  sectionUrl: PropTypes.string,
  showTrailingDisc: PropTypes.bool,
  awardsYear: PropTypes.string,
  index: PropTypes.string,
  currentCategory: PropTypes.string,
  size: PropTypes.string,
  shouldTrack: PropTypes.bool,
};

AwardsNavigationBox.defaultProps = {
  section: '',
  sectionUrl: '',
  showTrailingDisc: false,
};

AwardsNavigationBox.getEditingConfig = (componentDocument) => {
  const trailingDiscText =
    componentDocument?.props?.awardsYear === '2022'
      ? 'Show disc between navigation links?'
      : 'Show trailing disc after navigation link?';
  return [
    stringInputSpec('section', 'Awards section'),
    stringInputSpec('sectionUrl', 'Awards section URL'),
    boolCheckboxSpec('showTrailingDisc', trailingDiscText),
  ];
};

AwardsNavigationBox.displayName = 'AwardsNavigationBox';

export default connect(
  createStructuredSelector({
    size: _.property('mediaQuery'),
  })
)(AwardsNavigationBox);
